import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Kinetic";
import EditorialMarquee from "@/components/Marquee";
import TourTeaser from "@/components/TourTeaser";
import { api } from "@/lib/api";

const HERO_IMG =
  "https://customer-assets-v7afamib.emergentagent.net/job_soundwave-shop-10/artifacts/3p2bphn4_IMG_3187.PNG";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const [products, setProducts] = useState([]);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.slice(0, 3))).catch(() => {});
    api.get("/gallery").then((r) => setPhotos(r.data.slice(-3).reverse())).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <section ref={heroRef} className="relative flex h-screen items-end overflow-hidden">
        <motion.img
          src={HERO_IMG}
          alt="Mike Millzz — GET-LOW artwork"
          data-testid="hero-background-image"
          style={{ y: bgY }}
          className="absolute inset-0 h-[120%] w-full object-cover object-[center_18%]"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-black/40" />
        <div className="absolute inset-0 bg-primary/10 mix-blend-overlay" />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24">
          <motion.p
            data-testid="hero-overline"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mb-6 font-mono text-xs tracking-[0.4em] text-primary"
          >
            @MIKE_MILLZZ_ — SOUTHERN SMOKE MUSIC GROUP
          </motion.p>
          <h1 data-testid="hero-title" className="font-anton leading-[0.85]">
            <MaskedLine className="text-[20vw] text-white md:text-[13vw]" delay={0.1}>
              MIKE
            </MaskedLine>
            <MaskedLine className="text-outline glow-blue text-[20vw] md:text-[13vw]" delay={0.28}>
              MILLZZ
            </MaskedLine>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/music"
              data-testid="hero-listen-button"
              className="group flex items-center gap-3 bg-primary px-8 py-4 font-mono text-sm font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0px_#fff]"
            >
              LISTEN NOW
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/merch"
              data-testid="hero-shop-button"
              className="group flex items-center gap-3 border border-white/40 px-8 py-4 font-mono text-sm font-bold tracking-[0.2em] text-white transition-colors duration-300 hover:border-primary hover:text-primary"
            >
              SHOP MERCH
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </motion.div>
        </div>
      </section>

      <EditorialMarquee />

      <section data-testid="home-merch-section" className="mx-auto max-w-7xl px-6 py-32">
        <Reveal>
          <div className="mb-16 flex items-end justify-between">
            <div>
              <p className="mb-3 font-mono text-xs tracking-[0.4em] text-primary">01 / THE DROP</p>
              <h2 className="font-anton text-5xl text-white md:text-7xl">LATEST MERCH</h2>
            </div>
            <Link
              to="/merch"
              data-testid="home-merch-viewall"
              className="hidden items-center gap-2 font-mono text-xs tracking-[0.25em] text-white/60 transition-colors duration-300 hover:text-primary md:flex"
            >
              VIEW ALL <ArrowUpRight size={14} />
            </Link>
          </div>
        </Reveal>

        <div className="grid gap-6 md:grid-cols-3">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.12}>
              <Link
                to="/merch"
                data-testid={`home-product-card-${p.id}`}
                className="group block border border-white/10 bg-card"
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="h-full w-full object-cover grayscale transition-[transform,filter] duration-700 group-hover:scale-105 group-hover:grayscale-0"
                  />
                  <div className="absolute inset-0 bg-primary/0 transition-colors duration-500 group-hover:bg-primary/10" />
                  <span className="absolute left-4 top-4 bg-black/70 px-3 py-1 font-mono text-[10px] tracking-[0.25em] text-primary backdrop-blur">
                    {p.tag}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-white/10 p-5">
                  <span className="font-oswald text-lg font-bold tracking-wider text-white">{p.name}</span>
                  <span className="font-mono text-sm text-primary">${p.price.toFixed(2)}</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {photos.length > 0 && (
        <section data-testid="home-gallery-strip" className="border-y border-white/10">
          <div className="mx-auto max-w-7xl px-6 py-24">
            <Reveal>
              <div className="mb-12 flex items-end justify-between">
                <div>
                  <p className="mb-3 font-mono text-xs tracking-[0.4em] text-primary">FRESH FROM THE CAMP</p>
                  <h2 className="font-anton text-5xl text-white md:text-7xl">LATEST SHOTS</h2>
                </div>
                <Link
                  to="/gallery"
                  data-testid="home-gallery-link"
                  className="hidden items-center gap-2 font-mono text-xs tracking-[0.25em] text-white/60 transition-colors duration-300 hover:text-primary md:flex"
                >
                  FULL GALLERY <ArrowUpRight size={14} />
                </Link>
              </div>
            </Reveal>
            <div className="grid gap-4 md:grid-cols-3">
              {photos.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.1}>
                  <Link
                    to="/gallery"
                    data-testid={`home-gallery-photo-${i}`}
                    className="group block overflow-hidden border border-white/10"
                  >
                    <img
                      src={p.url}
                      alt={p.caption || `Mike Millzz gallery photo ${i + 1}`}
                      loading="lazy"
                      className="aspect-square w-full object-cover grayscale transition-[transform,filter] duration-700 group-hover:scale-105 group-hover:grayscale-0"
                    />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section data-testid="home-manifesto-teaser" className="border-y border-white/10 bg-card">
        <div className="mx-auto max-w-7xl px-6 py-32">
          <Reveal>
            <p className="mb-6 font-mono text-xs tracking-[0.4em] text-primary">02 / THE MANIFESTO</p>
            <blockquote className="max-w-4xl font-oswald text-3xl font-medium leading-snug text-white md:text-5xl">
              "FROM THE SMOKE CAME A SOUND. FROM THE SOUND CAME A MOVEMENT.{" "}
              <span className="text-primary">THE SOUTH AIN'T WHISPERING NO MORE.</span>"
            </blockquote>
            <Link
              to="/about"
              data-testid="home-manifesto-link"
              className="mt-10 inline-flex items-center gap-3 font-mono text-xs tracking-[0.3em] text-white/60 transition-colors duration-300 hover:text-primary"
            >
              READ THE FULL STORY <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      <TourTeaser />

      <EditorialMarquee
        outline
        speed={35}
        items={["GET-LOW", "OUT NOW", "STREAM EVERYWHERE", "SOUTHERN SMOKE"]}
      />

      <section data-testid="home-subscribe-cta" className="mx-auto max-w-7xl px-6 py-32">
        <Reveal className="relative overflow-hidden border border-primary/30 bg-black p-10 md:p-20">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary/20 blur-[100px]" />
          <p className="mb-4 font-mono text-xs tracking-[0.4em] text-primary">03 / THE CIRCLE</p>
          <h2 className="font-anton text-5xl leading-none text-white md:text-7xl">
            JOIN THE <span className="text-primary glow-blue">MILLZZ CIRCLE</span>
          </h2>
          <p className="mt-6 max-w-xl text-base text-white/60 md:text-lg">
            Unreleased tracks, early merch drops, and members-only freestyles. Free fan list or go all in with a paid membership.
          </p>
          <Link
            to="/subscribe"
            data-testid="home-subscribe-button"
            className="mt-10 inline-flex items-center gap-3 bg-primary px-8 py-4 font-mono text-sm font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0px_#fff]"
          >
            GET ACCESS <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
